import {
  Button,
  PocketRow,
  PocketSection,
  PocketStatement,
} from "@/components/ui";
import { formatDate, formatNumber, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsUsageMessages } from "@/i18n/messages/settings-usage";
import { useMonthlyUsage } from "./usage-state";
function usageDollars(cents: number) {
  return formatNumber(cents / 100, { style: "currency", currency: "USD" });
}
export function UsagePage() {
  const t = useT(settingsUsageMessages);
  const common = useT(commonMessages);
  const usage = useMonthlyUsage();
  return (
    <div data-testid="settings-page-usage">
      <PocketStatement
        size="panel"
        title={
          usage.loading
            ? t("loadingUsage")
            : usage.summary
              ? t("usageTotal", {
                  tokens: formatNumber(usage.summary.total_tokens),
                })
              : t("usageUnavailable")
        }
        line={
          usage.summary
            ? t("usageCost", {
                cost: usageDollars(usage.summary.total_cost_cents),
              })
            : undefined
        }
      />
      {usage.error && (
        <div className="px-2">
          <p role="alert" className="text-appkit-small text-danger">
            {t(usage.error)}
          </p>
          <Button
            size="sm"
            variant="ghost"
            data-testid="usage-retry"
            onClick={() =>
              void (usage.summary ? usage.loadMore() : usage.reload())
            }
          >
            {common("retry")}
          </Button>
        </div>
      )}
      {usage.summary && (
        <PocketSection title={t("runsThisMonth")}>
          {usage.rows.length === 0 && (
            <p
              className="px-2 py-3 text-appkit-body text-muted"
              data-testid="usage-empty"
            >
              {t("noRuns")}
            </p>
          )}
          {usage.rows.map((row) => (
            <PocketRow
              key={row.runtime_session_id}
              testId={`usage-run-${row.runtime_session_id}`}
              title={`${row.project_name}${row.agent_name ? ` · ${row.agent_name}` : ""}`}
              secondLine={
                (row.models?.length
                  ? row.models
                  : row.model
                    ? [row.model]
                    : []
                ).join(", ") || t("modelNotRecorded")
              }
              trailing={usageDollars(row.total_cost_cents)}
              below={
                <span className="text-appkit-small text-muted">
                  {t("runTokens", {
                    input: formatNumber(row.total_input_tokens),
                    output: formatNumber(row.total_output_tokens),
                    started: formatDate(row.started_at, {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    }),
                  })}
                </span>
              }
            />
          ))}
          {usage.nextOffset !== null && (
            <PocketRow
              testId="usage-show-more"
              title={usage.more ? common("loading") : t("showMore")}
              disabled={usage.more}
              onSelect={() => void usage.loadMore()}
            />
          )}
        </PocketSection>
      )}
    </div>
  );
}
